import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchTokensQuery } from '../redux/api/public/tokens';
import { PublicKey } from '@solana/web3.js';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectTokensWithUSDBalance,
  selectAllTokens,
} from '../redux/slices/tokenSlice';

const CATEGORY_URL_PARAM = 'category';

// Helper to match token by address and network
const tokenKey = (token) => `${token.network || 'solana'}:${token.address}`;

// Helper function to check if a token has a specific category
const hasCategory = (token, categoryName) => {
  return token.categories?.some((cat) => cat.name === categoryName) ?? false;
};

// Selector to get tokens by their keys from Redux
const selectTokensByKeys = createSelector(
  [selectAllTokens, (_state, tokenKeys) => tokenKeys],
  (allTokens, tokenKeys) => {
    if (!tokenKeys || tokenKeys.size === 0) return [];
    const tokenMap = new Map(allTokens.map((token) => [tokenKey(token), token]));
    return Array.from(tokenKeys)
      .map((key) => tokenMap.get(key))
      .filter(Boolean);
  },
);

export const useTokenSearch = (customBaseTokens = null, filterToken = null) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get(CATEGORY_URL_PARAM);
  const initialCategory = categoryFromUrl || 'ALL';

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategoryState] = useState(initialCategory);
  const [sortByCategory, setSortByCategory] = useState(null);

  // Sync selectedCategory from URL when user navigates (e.g. back/forward)
  useEffect(() => {
    const urlCategory = searchParams.get(CATEGORY_URL_PARAM);
    setSelectedCategoryState(urlCategory || 'ALL');
  }, [searchParams]);

  const setSelectedCategory = useCallback(
    (category) => {
      setSelectedCategoryState(category);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (category === 'ALL') {
            next.delete(CATEGORY_URL_PARAM);
          } else {
            next.set(CATEGORY_URL_PARAM, category);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  // Track token keys per query (for pagination tracking)
  const [queryTokenKeys, setQueryTokenKeys] = useState(new Set());
  const tokensWithBalance = useSelector((state) =>
    selectTokensWithUSDBalance(state),
  );
  const allReduxTokens = useSelector(selectAllTokens);

  // Use custom base tokens if provided, otherwise use tokens with balance
  const baseTokens = customBaseTokens || tokensWithBalance;

  // If custom tokens are provided, we should only search within them (no API calls)
  const shouldUseOnlyCustomTokens = customBaseTokens !== null;

  // Use Solana's built-in validation to check if search term is a valid address
  const isAddressSearch = useCallback((term) => {
    if (!term || term.length < 32 || term.length > 44) return false;

    try {
      new PublicKey(term);
      return true; // If no error was thrown, it's a valid address
    } catch {
      return false; // PublicKey constructor failed, not a valid address
    }
  }, []);

  // Create a query key based on API parameters for caching
  const queryKey = useMemo(() => {
    const name = !isAddressSearch(debouncedSearchTerm)
      ? debouncedSearchTerm || ''
      : '';
    const addresses = isAddressSearch(debouncedSearchTerm)
      ? debouncedSearchTerm
      : '';
    const category = selectedCategory !== 'ALL' ? selectedCategory : '';
    return JSON.stringify({ name, addresses, category });
  }, [debouncedSearchTerm, selectedCategory, isAddressSearch]);

  // Main query - fetches all tokens (or filtered by selectedCategory)
  const {
    data: searchData,
    isLoading,
    isFetching,
    error,
  } = useSearchTokensQuery(
    {
      name: !isAddressSearch(debouncedSearchTerm)
        ? debouncedSearchTerm || undefined
        : undefined,
      addresses: isAddressSearch(debouncedSearchTerm)
        ? [debouncedSearchTerm]
        : undefined,
      category: selectedCategory !== 'ALL' ? selectedCategory : undefined,
      pageNo,
      pageAmount: 50,
    },
    {
      skip: shouldUseOnlyCustomTokens, // Skip API calls when using only custom tokens
    },
  );

  // Query to fetch all tokens of the sortByCategory when set
  const {
    data: categorySearchData,
    isLoading: isCategoryLoading,
    isFetching: isCategoryFetching,
  } = useSearchTokensQuery(
    {
      category: sortByCategory || undefined,
      pageNo: 0,
      pageAmount: 1000,
    },
    {
      skip: shouldUseOnlyCustomTokens || !sortByCategory,
    },
  );

  // Debounce the search term for API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Track token keys from API responses for pagination
  useEffect(() => {
    if (shouldUseOnlyCustomTokens) {
      setHasMore(false);
      return;
    }

    if (searchData?.tokens) {
      setQueryTokenKeys((prev) => {
        const newKeys = new Set(prev);
        searchData.tokens.forEach((token) => {
          newKeys.add(tokenKey(token));
        });
        return newKeys;
      });

      // Update pagination state
      const totalCount = searchData.count || 0;
      const currentCount = (pageNo + 1) * 50;
      setHasMore(currentCount < totalCount);
    }

    // Also track category tokens if sortByCategory is set
    if (sortByCategory && categorySearchData?.tokens) {
      setQueryTokenKeys((prev) => {
        const newKeys = new Set(prev);
        categorySearchData.tokens.forEach((token) => {
          newKeys.add(tokenKey(token));
        });
        return newKeys;
      });
    }
  }, [
    shouldUseOnlyCustomTokens,
    searchData,
    categorySearchData,
    pageNo,
    sortByCategory,
  ]);


  // Reset pagination when query changes (search, category) or sortByCategory changes
  useEffect(() => {
    setPageNo(0);
    setQueryTokenKeys(new Set());
  }, [queryKey, sortByCategory]);


  // Load more tokens (for infinite scroll)
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setPageNo((prev) => prev + 1);
    }
  }, [hasMore, isFetching]);

  // Get tokens from Redux using selector (memoized automatically)
  const tokensFromQuery = useSelector((state) =>
    selectTokensByKeys(state, queryTokenKeys),
  );

  // Compute displayTokens using selectors
  const displayTokens = useMemo(() => {
    // Case 1: Custom tokens only
    if (shouldUseOnlyCustomTokens) {
      let tokens = baseTokens;

      if (filterToken) {
        tokens = tokens.filter(
          (token) => token.address !== filterToken.address,
        );
      }

      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        tokens = tokens.filter(
          (token) =>
            token.name?.toLowerCase().includes(lowerSearchTerm) ||
            token.symbol?.toLowerCase().includes(lowerSearchTerm) ||
            token.address?.toLowerCase().includes(lowerSearchTerm),
        );
      }

      return tokens;
    }

    // Case 2: API-based search
    // Start with baseTokens
    const baseTokenKeys = new Set(baseTokens.map(tokenKey));
    let tokens = [...baseTokens];

    // Add tokens from query (from Redux)
    const queryTokenSet = new Set(queryTokenKeys);
    tokensFromQuery.forEach((token) => {
      const key = tokenKey(token);
      if (!baseTokenKeys.has(key) && queryTokenSet.has(key)) {
        tokens.push(token);
      }
    });

    // If no query tokens yet but we have searchData, use it as fallback
    if (queryTokenKeys.size === 0 && searchData?.tokens) {
      const existingKeys = new Set(tokens.map(tokenKey));
      searchData.tokens.forEach((token) => {
        const key = tokenKey(token);
        if (!existingKeys.has(key) && !baseTokenKeys.has(key)) {
          tokens.push(token);
        }
      });
    }

    // Apply search filter if needed
    if (debouncedSearchTerm) {
      const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
      tokens = tokens.filter(
        (token) =>
          token.name?.toLowerCase().includes(lowerSearchTerm) ||
          token.symbol?.toLowerCase().includes(lowerSearchTerm) ||
          token.address?.toLowerCase().includes(lowerSearchTerm),
      );
    }

    // Apply category filter if needed
    if (selectedCategory !== 'ALL') {
      tokens = tokens.filter((token) =>
        token.categories?.some((cat) => cat.name === selectedCategory),
      );
    }

    // Apply sorting by category if needed
    if (sortByCategory) {
      tokens = [...tokens].sort((a, b) => {
        const aHasCategory = hasCategory(a, sortByCategory);
        const bHasCategory = hasCategory(b, sortByCategory);
        if (aHasCategory && !bHasCategory) return -1;
        if (!aHasCategory && bHasCategory) return 1;
        return 0;
      });
    }

    // Filter out excluded token
    if (filterToken) {
      tokens = tokens.filter(
        (token) => token.address !== filterToken.address,
      );
    }

    return tokens;
  }, [
    shouldUseOnlyCustomTokens,
    baseTokens,
    queryTokenKeys,
    tokensFromQuery,
    searchData,
    debouncedSearchTerm,
    selectedCategory,
    sortByCategory,
    filterToken,
    searchTerm,
  ]);

  return {
    // State
    searchTerm,
    selectedCategory,
    sortByCategory,
    displayTokens,
    isLoading: shouldUseOnlyCustomTokens
      ? false
      : isLoading || isCategoryLoading,
    isFetching: shouldUseOnlyCustomTokens
      ? false
      : isFetching || isCategoryFetching,
    hasMore,
    error,

    // Actions
    setSearchTerm,
    setSelectedCategory,
    setSortByCategory,
    loadMore,

    // Computed
    totalCount: shouldUseOnlyCustomTokens
      ? displayTokens.length
      : searchData?.count || 0,
  };
};
